import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import api from "../api";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export default function CityDetail() {
  const { cityId } = useParams();
  const navigate = useNavigate();

  const [city, setCity] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState("month");
  const [currency, setCurrency] = useState(null);
  const [domesticCurrency, setDomesticCurrency] = useState(null);
  const [timeRange, setTimeRange] = useState("12m");

  const canvasRef = useRef(null);

  const categoryOrder = ["Studio", "Single-bedroom", "Two-bedroom", "Three-bedroom"];
  const categoryKeyMap = { Studio: "Studio", "Single-bedroom": "Single", "Two-bedroom": "Double", "Three-bedroom": "Triple+" };
  const categoryColors = { Studio: "#4ade80", "Single-bedroom": "#60a5fa", "Two-bedroom": "#f472b6", "Three-bedroom": "#fbbf24" };

  const formatNumber = (val) => (val != null ? Number(val).toLocaleString() : "-");
  const formatCurrency = (val) => (val != null ? `${currency} ${Number(val).toLocaleString()}` : "-");

  // Fetch city and stats
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const cityRes = await api.get(`/housing/cities/${cityId}/`);
        setCity(cityRes.data);

        const domestic = cityRes.data?.country?.currency || "USD";
        setDomesticCurrency(domestic);
        setCurrency(currency || domestic);

        const statsRes = await api.get(
          `/housing/city_stats/${cityId}/?range=${range}&currency=${currency || domestic}&time_range=${timeRange}`
        );
        setStats(statsRes.data);
      } catch (err) {
        console.error(err);
        setError("Could not load city or housing data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [cityId, range, currency, timeRange]);

  // Draw static scatterplot with axes, labels, and legend
  useEffect(() => {
    if (!stats || !canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");
    const width = canvasRef.current.width;
    const height = canvasRef.current.height;

    ctx.clearRect(0, 0, width, height);

    // Prepare points
    const allPoints = [];
    stats.scatterplot.forEach((p) => {
      const rooms = Number(p.rooms);
      const size = Number(p.size);
      const price = Number(p.price_per_sqm);
      if (!isFinite(rooms) || !isFinite(size) || !isFinite(price)) return;

      let cat = null;
      if (rooms === 1) cat = "Studio";
      else if (rooms === 2) cat = "Single-bedroom";
      else if (rooms === 3) cat = "Two-bedroom";
      else if (rooms >= 4) cat = "Three-bedroom";

      if (cat) allPoints.push({ x: size, y: price, color: categoryColors[cat], category: cat });
    });

    if (allPoints.length === 0) return;

    const margin = 50;
    const innerWidth = width - margin - 20;
    const innerHeight = height - margin - 20;

    const xMin = Math.min(...allPoints.map((p) => p.x));
    const xMax = Math.max(...allPoints.map((p) => p.x));
    const yMin = Math.min(...allPoints.map((p) => p.y));
    const yMax = Math.max(...allPoints.map((p) => p.y));

    // Draw axes
    ctx.strokeStyle = "#333";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(margin, height - margin);
    ctx.lineTo(width - 20, height - margin); // X axis
    ctx.moveTo(margin, height - margin);
    ctx.lineTo(margin, 20); // Y axis
    ctx.stroke();

    // Draw points
    allPoints.forEach((p) => {
      const cx = margin + ((p.x - xMin) / (xMax - xMin)) * innerWidth;
      const cy = height - margin - ((p.y - yMin) / (yMax - yMin)) * innerHeight;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(cx, cy, 3, 0, 2 * Math.PI);
      ctx.fill();
    });

    // X ticks
    ctx.fillStyle = "#000";
    ctx.font = "12px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    const xTicks = 5;
    for (let i = 0; i <= xTicks; i++) {
      const xVal = xMin + (i * (xMax - xMin)) / xTicks;
      const cx = margin + (i * innerWidth) / xTicks;
      ctx.beginPath();
      ctx.moveTo(cx, height - margin);
      ctx.lineTo(cx, height - margin + 5);
      ctx.stroke();
      ctx.fillText(Math.round(xVal), cx, height - margin + 7);
    }

    // Y ticks
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    const yTicks = 5;
    for (let i = 0; i <= yTicks; i++) {
      const yVal = yMin + (i * (yMax - yMin)) / yTicks;
      const cy = height - margin - (i * innerHeight) / yTicks;
      ctx.beginPath();
      ctx.moveTo(margin, cy);
      ctx.lineTo(margin - 5, cy);
      ctx.stroke();
      ctx.fillText(Math.round(yVal), margin - 7, cy);
    }

    // Axis labels
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText("Apartment Size (sqm)", margin + innerWidth / 2, height - 5);

    ctx.save();
    ctx.translate(15, margin + innerHeight / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(`Price per sqm (${currency})`, 0, 0);
    ctx.restore();

    // Legend
    const legendX = width - 130;
    const legendY = 30;
    ctx.font = "12px sans-serif";
    categoryOrder.forEach((cat, idx) => {
      ctx.fillStyle = categoryColors[cat];
      ctx.fillRect(legendX, legendY + idx * 20, 12, 12);
      ctx.fillStyle = "#000";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(cat, legendX + 18, legendY + idx * 20 + 6);
    });
  }, [stats, currency, categoryColors, categoryOrder]);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-100 bg-opacity-80 z-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-b-4 border-gray-200"></div>
      </div>
    );
  }

  if (error) return <p className="p-6 text-red-600">{error}</p>;

  // Median rent line chart data
  const medianRentData = stats?.median_rent_over_time?.labels.map((label, i) => {
    const dataPoint = { period: label };
    categoryOrder.forEach((displayLabel) => {
      const backendKey = categoryKeyMap[displayLabel];
      const ds = stats.median_rent_over_time.datasets.find((d) => d.label === backendKey);
      dataPoint[displayLabel] = ds?.data[i] ?? null;
    });
    return dataPoint;
  });

  const sizeCharts = stats?.size_distributions || {};
  const priceCharts = stats?.price_distributions || {};

  const ChartBox = ({ children }) => <div className="bg-white shadow-md p-4 rounded-lg">{children}</div>;

  const renderHistogram = (data, label, xLabel) => (
    <div className="w-full" key={label}>
      <h3 className="text-lg font-semibold mb-2">{label}</h3>
      {data.length === 0 ? (
        <p className="text-gray-500 text-sm">No data available.</p>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="min" tick={{ fontSize: 10 }} tickFormatter={(v) => formatNumber(Math.round(v))} label={{ value: xLabel, position: "insideBottom", offset: -5 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => formatNumber(v)} label={{ value: "Count", angle: -90, position: "insideLeft" }} />
            <Tooltip formatter={(value, name, props) => `${formatNumber(Math.round(props.payload.min))}–${formatNumber(Math.round(props.payload.max))}: ${formatNumber(Math.round(value))}`} />
            <Bar dataKey="count" fill={categoryColors[label] || "#888888"} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="p-4 max-w-6xl mx-auto space-y-6">
        <button onClick={() => navigate("/cities")} className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-3 py-1 rounded-md transition-colors">
          ← Back to Map
        </button>

        {/* City info + filters + line chart */}
        <ChartBox>
          <div className="flex flex-col md:flex-row md:items-start md:space-x-6">
            <div className="flex-1 space-y-2">
              <h1 className="text-3xl font-bold">{city.name}</h1>
              <p className="text-gray-700">
                <strong>Country:</strong> {city.country?.name || "Unknown"} |{" "}
                <strong>Currency:</strong>{" "}
                <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="border rounded px-2 py-1 ml-1">
                  {domesticCurrency && <option value={domesticCurrency}>Domestic ({domesticCurrency})</option>}
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="JPY">JPY</option>
                </select>{" "}
                | <strong>Status:</strong> {city.is_available ? "Available" : "Unavailable"}
              </p>
              <div className="flex space-x-2">
                <div>
                  <label className="mr-1 font-medium">View median rent by:</label>
                  <select value={range} onChange={(e) => setRange(e.target.value)} className="border rounded px-2 py-1">
                    <option value="month">Month</option>
                    <option value="quarter">Quarter</option>
                  </select>
                </div>
                <div>
                  <label className="mr-1 font-medium">Time Range:</label>
                  <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="border rounded px-2 py-1">
                    <option value="all">All Time</option>
                    <option value="24m">Last 2 Years</option>
                    <option value="12m">Last Year</option>
                    <option value="6m">Last 6 Months</option>
                    <option value="3m">Last Quarter</option>
                    <option value="1m">Last Month</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex-1 mt-4 md:mt-0">
              <h2 className="text-2xl font-semibold mb-2">Median Rent Over Time</h2>
              {medianRentData?.length === 0 ? (
                <p className="text-gray-500 text-sm">No median rent data.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={medianRentData} margin={{ top: 10, right: 20, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" label={{ value: "Time Period", position: "insideBottom", offset: -20 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => formatNumber(v)} label={{ value: `Rent (${currency})`, angle: -90, position: "insideLeft" }} />
                    <Tooltip formatter={(value) => (value != null ? formatCurrency(Math.round(value)) : "-")} />
                    <Legend verticalAlign="top" align="right" wrapperStyle={{ width: "70%", paddingTop: "4px", paddingRight: "8px", background: "rgba(255,255,255,0.85)", borderRadius: "6px", fontSize: "0.85em" }} />
                    {categoryOrder.map((displayLabel) => (
                      <Line key={displayLabel} type="monotone" dataKey={displayLabel} stroke={categoryColors[displayLabel]} strokeWidth={2} dot={{ r: 2 }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </ChartBox>

        {/* Size + Price distributions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ChartBox>
            <h2 className="text-2xl font-semibold mb-3">Size Distributions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {categoryOrder.map((displayLabel) => renderHistogram(sizeCharts[categoryKeyMap[displayLabel]] || [], displayLabel, "Size (sqm)"))}
            </div>
          </ChartBox>
          <ChartBox>
            <h2 className="text-2xl font-semibold mb-3">Price Distributions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {categoryOrder.map((displayLabel) => renderHistogram(priceCharts[categoryKeyMap[displayLabel]] || [], displayLabel, `Rent (${currency})`))}
            </div>
          </ChartBox>
        </div>

        {/* Static scatterplot */}
        <ChartBox>
          <h2 className="text-2xl font-semibold mb-3">Price per sqm vs Apartment Size</h2>
          <canvas ref={canvasRef} width={800} height={350} className="w-full border border-gray-300" />
        </ChartBox>

        {/* Rent statistics table */}
        <ChartBox>
          <h2 className="text-2xl font-semibold mb-3">Rent Statistics</h2>
          {stats?.rent_stats ? (
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto border border-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border px-2 py-1">Category</th>
                    {["P10", "P20", "P25", "P50", "P75", "Median", "Count"].map((l) => (
                      <th key={l} className="border px-2 py-1">{l}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {categoryOrder.map((displayLabel) => {
                    const backendKey = categoryKeyMap[displayLabel];
                    const row = stats.rent_stats?.[backendKey] || {};
                    return (
                      <tr key={displayLabel} className="text-center">
                        <td className="border px-2 py-1 font-medium">{displayLabel}</td>
                        {["P10", "P20", "P25", "P50", "P75", "median", "count"].map((k) => (
                          <td key={k} className="border px-2 py-1">
                            {row[k] !== null && row[k] !== undefined
                              ? k === "count"
                                ? formatNumber(row[k])
                                : formatCurrency(Math.round(row[k]))
                              : "-"}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No rent statistics available.</p>
          )}
        </ChartBox>
      </div>
    </div>
  );
}
